import { Box, Flex, Heading, Spinner, Text, useDisclosure } from '@chakra-ui/react';
import { useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslations } from 'use-intl';

import { noop, routes } from '@blockpulse3/data/shared';
import {
  GetSpvDraftQuery,
  useCreateSpvDraftMutation,
  useDeleteCompanyDraftMutation,
  useGetSpvDraftQuery,
  useUpdateSpvDraftMutation,
} from '@blockpulse3/graphql/hooks';
import {
  DeleteDraftConfirmModal,
  StepHeader,
  useErrorToast,
  useSuccessToast,
} from '@blockpulse3/ui/commons';

import { SPVFormWizard } from './OnboardingSPVWizard';
import { SPVFormContextProvider } from './OnboardingSPVWizard/provider';
import { SubmitScreen } from './SubmitScreen';

type Props = {
  onSubmitted?: () => void;
};

/**
 * SPVView.
 * Main component for the creation of a Special Purpose Vehicle. Wrap the main wizard
 * form with the dedicated provider.
 *
 * @returns {JSX.Element}
 */
export function OnboardingSPVView(): JSX.Element {
  const i18nOnboardingSPVView = useTranslations('OnboardingSPVView');

  const cancelRef = useRef(null);

  const errorToast = useErrorToast();
  const successToast = useSuccessToast();

  const navigate = useNavigate();
  const { id: urlId = '' } = useParams();

  const [id, setId] = useState<string>(urlId);
  const [isNew, setIsNew] = useState<boolean>(false);
  const [isSubmit, setIsSubmit] = useState<boolean>(false);

  const { data, loading } = useGetSpvDraftQuery({ variables: { id }, skip: !id || isNew });

  const [updateSPVDraft] = useUpdateSpvDraftMutation();
  const [createSPVDraft] = useCreateSpvDraftMutation();
  const [deleteSPVDraft] = useDeleteCompanyDraftMutation();

  /* ** Draft confirm modal ** */
  const { isOpen, onOpen, onClose } = useDisclosure();

  const handleWizardChange = (
    content: GetSpvDraftQuery['spvDraft']['content'],
    onSuccess = noop,
  ): void => {
    updateSPVDraft({
      variables: {
        updateSPVDraftInput: {
          companyDraftId: id,
          content,
        },
      },
      onCompleted() {
        onSuccess();
      },
      onError: () => {
        errorToast({ title: i18nOnboardingSPVView('Toast.Error') });
      },
    });
  };

  const handleWizardCreate = (content: GetSpvDraftQuery['spvDraft']['content']): void => {
    createSPVDraft({
      variables: {
        createSPVDraftInput: {
          content,
        },
      },
      onCompleted: (data) => {
        const id = data.createSPVDraft.id;
        setIsNew(true);
        setId(id);
        /* eslint-disable-next-line no-restricted-globals */
        history.pushState(null, '', routes.onboardings.spvEdit.fmt(id));
      },
    });
  };

  const handleWizardSubmit = (): void => {
    setIsSubmit(true);
    setTimeout(() => {
      setIsSubmit(false);
      navigate(routes.onboardings.spv.fmt(id));
    }, 3000);
  };

  const handleWizardDelete = (): void => {
    if (id) {
      deleteSPVDraft({
        variables: {
          id,
        },
        onCompleted: (res) => {
          if (res.deleteCompanyDraft) {
            navigate(routes.me.href);
            successToast({ title: i18nOnboardingSPVView('Toast.Success') });
          } else {
            errorToast({ title: i18nOnboardingSPVView('Toast.Error') });
          }
        },
        onError: () => {
          errorToast({ title: i18nOnboardingSPVView('Toast.Error') });
        },
      });
    }
  };

  const handleWizardClose = async (): Promise<void> => {
    /* ** Refetch WorkspaceSwitcher items ** */
    navigate(routes.me.href);
  };

  return (
    <Flex direction="column" minH="100vh">
      <DeleteDraftConfirmModal
        isOpen={isOpen}
        leastDestructiveRef={cancelRef}
        onClose={onClose}
        onDelete={handleWizardDelete}
      />
      <StepHeader p="4" showDelete={!!id} onClose={handleWizardClose} onDelete={onOpen}>
        <Heading size="lg">{i18nOnboardingSPVView('WizardTitle')}</Heading>
        <Text color="gray.600" fontWeight="normal">
          {i18nOnboardingSPVView('WizardSubTitle')}
        </Text>
      </StepHeader>
      <Box bg="gray.50" flexGrow="1" h="full">
        {loading ? (
          <Spinner />
        ) : (
          <SPVFormContextProvider defaultValues={data?.spvDraft.content} draftId={id}>
            {isSubmit ? (
              <SubmitScreen />
            ) : (
              <SPVFormWizard
                onChange={handleWizardChange}
                onCreate={handleWizardCreate}
                onSubmit={handleWizardSubmit}
              />
            )}
          </SPVFormContextProvider>
        )}
      </Box>
    </Flex>
  );
}

export type OnboardingSPVViewProps = Props;
